import { useRef, useEffect, useState } from 'react';
import { longitudeToSvgPoint, formatPosition } from './chartMath';

// ─── Constants ────────────────────────────────────────────────────────────────

const CX = 150;
const CY = 150;
const OUTER_R = 130;
const INNER_R = OUTER_R * 0.45; // ~58.5
const LABEL_R = OUTER_R * 0.72; // ~93.6

const DASHARRAY = {
  solid:     'none',
  dashed:    '4,4',
  'dot-dash': '2,6',
  dotted:    '1,3',
};

// ─── glowIntensity ────────────────────────────────────────────────────────────

/**
 * Interpolate visual intensity based on gears unlocked (0–7).
 * Returns { ringOpacity, ringGlow, lineOpacity, innerOpacity }
 */
function glowIntensity(gearsUnlocked) {
  const t = Math.max(0, Math.min(7, gearsUnlocked)) / 7;
  return {
    ringOpacity:  0.15 + t * 0.85,   // 0.15 → 1.0
    ringGlow:     t === 1,            // full filter only when complete
    lineOpacity:  0.05 + t * 0.35,   // 0.05 → 0.40
    innerOpacity: 0.10 + t * 0.55,   // 0.10 → 0.65
  };
}

// ─── StarMapWheel ─────────────────────────────────────────────────────────────

export default function StarMapWheel({
  placedPlanets = [],
  aspects = [],
  gearsUnlocked = 0,
  ghostPlanets = [],
  onPlanetClick,
}) {
  const glow = glowIntensity(gearsUnlocked);
  const isComplete = gearsUnlocked >= 7;

  // Track which planets are "new" for pulse animation
  const prevPlacedRef = useRef(new Set());
  const [newPlanetIds, setNewPlanetIds] = useState(new Set());

  useEffect(() => {
    const currentIds = new Set(placedPlanets.map(p => p.id));
    const freshIds = new Set();
    for (const id of currentIds) {
      if (!prevPlacedRef.current.has(id)) {
        freshIds.add(id);
      }
    }
    if (freshIds.size > 0) {
      setNewPlanetIds(freshIds);
      // Clear "new" status after animation duration
      const timer = setTimeout(() => setNewPlanetIds(new Set()), 2000);
      return () => clearTimeout(timer);
    }
    prevPlacedRef.current = currentIds;
  }, [placedPlanets.length]);

  // Update ref after render to track for next comparison
  useEffect(() => {
    prevPlacedRef.current = new Set(placedPlanets.map(p => p.id));
  }, [placedPlanets]);

  const ringStrokeWidth = isComplete ? 2.5 : 1 + (gearsUnlocked / 7);
  const ringFilter = isComplete ? 'url(#glow)' : 'none';

  // Pre-compute planet lookup for aspect endpoints
  const planetLonMap = {};
  for (const p of placedPlanets) {
    planetLonMap[p.id] = p.longitude;
  }

  // House division lines: 12 lines, every 30°
  const houseLines = Array.from({ length: 12 }, (_, i) => {
    const lon = i * 30;
    const isCardinal = i % 3 === 0;
    const inner = longitudeToSvgPoint(lon, CX, CY, INNER_R);
    const outer = longitudeToSvgPoint(lon, CX, CY, OUTER_R);
    return { lon, isCardinal, inner, outer };
  });

  // Center label text
  const centerLine1 = isComplete ? 'ALL GEARS' : `${gearsUnlocked} OF 7`;
  const centerLine2 = isComplete ? 'UNLOCKED \u2605' : '/ GEARS';

  // Outer ring glow at completion: more dramatic box-shadow
  const outerRingStyle = isComplete
    ? { filter: 'drop-shadow(0 0 6px rgba(0,255,65,0.5)) drop-shadow(0 0 12px rgba(0,255,65,0.25))' }
    : {};

  return (
    <div style={{
      background: '#050508',
      padding: '0.5rem',
      borderBottom: '2px solid #1a1a1a',
      flex: 1,
      minHeight: 0,
      position: 'relative',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Scanline overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,255,65,0.02) 3px, rgba(0,255,65,0.02) 4px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* SVG chart */}
      <svg
        viewBox="0 0 300 300"
        style={{ width: '100%', height: '100%', maxWidth: 300, maxHeight: 300, display: 'block', position: 'relative', zIndex: 1, ...outerRingStyle }}
      >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="planet-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ── Outer ring ── */}
        <circle
          cx={CX}
          cy={CY}
          r={OUTER_R}
          fill="none"
          stroke="#00ff41"
          strokeWidth={ringStrokeWidth}
          opacity={glow.ringOpacity}
          filter={ringFilter}
        />
        {/* Second outer glow ring at completion */}
        {isComplete && (
          <circle
            cx={CX}
            cy={CY}
            r={OUTER_R + 3}
            fill="none"
            stroke="#00ff41"
            strokeWidth={0.5}
            opacity={0.3}
            style={{ animation: 'pulse-glow 2s ease-in-out infinite' }}
          />
        )}

        {/* ── Inner circle ── */}
        <circle
          cx={CX}
          cy={CY}
          r={INNER_R}
          fill="none"
          stroke="#00ff41"
          strokeWidth={0.75}
          opacity={glow.innerOpacity}
        />

        {/* ── House division lines ── */}
        {houseLines.map(({ lon, isCardinal, inner, outer }) => (
          <line
            key={lon}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke="#00ff41"
            strokeWidth={isCardinal ? 0.75 : 0.4}
            opacity={isCardinal ? glow.lineOpacity * 1.6 : glow.lineOpacity}
          />
        ))}

        {/* ── Aspect lines ── */}
        {aspects.map((asp, i) => {
          const lon1 = planetLonMap[asp.planet1];
          const lon2 = planetLonMap[asp.planet2];
          if (lon1 == null || lon2 == null) return null;

          const p1 = placedPlanets.find(p => p.id === asp.planet1);
          const strokeColor = p1?.color ?? '#00ff41';

          const pt1 = longitudeToSvgPoint(lon1, CX, CY, INNER_R);
          const pt2 = longitudeToSvgPoint(lon2, CX, CY, INNER_R);
          const dash = DASHARRAY[asp.style] ?? 'none';

          return (
            <line
              key={`asp-${i}`}
              x1={pt1.x}
              y1={pt1.y}
              x2={pt2.x}
              y2={pt2.y}
              stroke={strokeColor}
              strokeWidth={0.75}
              strokeDasharray={dash}
              opacity={0.35}
            />
          );
        })}

        {/* ── Ghost planet labels ── */}
        {ghostPlanets.map((gp) => {
          const pt = longitudeToSvgPoint(gp.longitude, CX, CY, LABEL_R);
          return (
            <text
              key={`ghost-${gp.id}`}
              x={pt.x}
              y={pt.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={7}
              fontFamily="monospace"
              fill="#00ff41"
              opacity={0.08}
            >
              {gp.glyph} -- ---
            </text>
          );
        })}

        {/* ── Starburst effects for newly placed planets ── */}
        {placedPlanets.map((planet) => {
          if (!newPlanetIds.has(planet.id)) return null;
          const pt = longitudeToSvgPoint(planet.longitude, CX, CY, LABEL_R);
          const planetColor = planet.color ?? '#00ff41';
          return (
            <circle
              key={`burst-${planet.id}`}
              cx={pt.x}
              cy={pt.y}
              r={0}
              fill="none"
              stroke={planetColor}
              strokeWidth={1.5}
              opacity={0.5}
              style={{ animation: 'starburst 0.8s ease-out forwards' }}
            />
          );
        })}

        {/* ── Placed planet labels ── */}
        {placedPlanets.map((planet) => {
          const pt = longitudeToSvgPoint(planet.longitude, CX, CY, LABEL_R);
          const label = formatPosition(planet.glyph, planet.longitude);
          const isClickable = !!onPlanetClick;
          const isNew = newPlanetIds.has(planet.id);
          const planetColor = planet.color ?? '#00ff41';

          return (
            <text
              key={`planet-${planet.id}`}
              x={pt.x}
              y={pt.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={7}
              fontFamily="monospace"
              fill={planetColor}
              opacity={glow.ringOpacity}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                filter: `drop-shadow(0 0 6px ${planetColor})`,
                transformOrigin: `${pt.x}px ${pt.y}px`,
                animation: isNew ? 'planet-appear 0.8s ease-out' : 'none',
              }}
              onClick={isClickable ? () => onPlanetClick(planet.id) : undefined}
            >
              {label}
            </text>
          );
        })}

        {/* ── Center gear counter ── */}
        <text
          x={CX}
          y={CY - 6}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={8}
          fontFamily="monospace"
          fill="#00ff41"
          opacity={glow.innerOpacity}
          letterSpacing={1}
          style={isComplete ? {
            animation: 'pulse-glow 2s ease-in-out infinite',
            filter: 'drop-shadow(0 0 6px rgba(0,255,65,0.6))',
          } : {}}
        >
          {centerLine1}
        </text>
        <text
          x={CX}
          y={CY + 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={7}
          fontFamily="monospace"
          fill="#00ff41"
          opacity={glow.innerOpacity * 0.75}
          letterSpacing={1}
          style={isComplete ? {
            animation: 'pulse-glow 2s ease-in-out infinite',
            animationDelay: '0.3s',
            filter: 'drop-shadow(0 0 4px rgba(0,255,65,0.4))',
          } : {}}
        >
          {centerLine2}
        </text>
      </svg>
    </div>
  );
}
